<template>
	<app-title-bar
		:icon="['fas', 'fa-truck']"
		subtitle="Yard-App"
		title="Collection Item Detail"
		:badges="[collectionNumber, collection?.contract_number, collection?.account_name]">
		<app-button to="/yard-app/collection-items" icon="fa-circle-left"> Back </app-button>
	</app-title-bar>

	<div class="flex flex-col-reverse 2xl:flex-row gap-4">
		<app-panel class="grow" title="Item Details">
			<template #header>
				<div v-if="collection" class="flex items-center gap-3">
					<app-badge theme="info" size="small" class="gap-1 ml-2">
						<font-awesome-icon icon="fa-solid fa-truck" class="w-4" />
						<b>Collection Number:</b>
						<p>{{ collection?.collection_number }}</p>
					</app-badge>
					<app-badge theme="info" size="small" class="gap-1 ml-2">
						<font-awesome-icon icon="fa-solid fa-truck" class="w-4" />
						<b>Customer Name:</b>
						<p>{{ collection?.account_name }}</p>
					</app-badge>
				</div>
			</template>

			<app-alert v-if="!loading && items.length == 0" theme="warning">
				There are no items in this collection
			</app-alert>

			<collection-detail-table
				v-else
				:items="items"
				:loading="loading"
				@updated-items="
					(item, kit) => {
						updated(item, kit)
					}
				"
				@updated-note="
					(item, kit) => {
						updatedNotes(item, kit)
					}
				"
				@add-edit-damage-codes="addEditDamageCodes" />

			<div class="flex flex-col mt-5 gap-3">
				<div class="border rounded-md flex items-center py-1 px-3">
					<app-checkbox
						v-model="confirmed"
						name="quality_checked_by"
						wrapper="m-0"
						:true-value="true"
						:false-value="false"
						label="The collected items have been assessed and checked by a competent person" />
				</div>

				<div class="flex justify-between">
					<app-button
						v-if="collection"
						icon="fa-note-sticky"
						:disabled="loading"
						:theme="collection?.notes ? 'danger' : 'primary'"
						@click="editingColNote = collection">
						{{ collection?.notes ? 'Edit Collection Note' : 'Add Collection Note' }}
					</app-button>

					<div class="flex gap-3">
						<app-button icon="fa-save" :disabled="!changed" @click="save(true)">
							Save
						</app-button>
						<app-button
							icon="fa-computer"
							:disabled="!changed || !confirmed"
							@click="sendInterceptor">
							Send to Hire Desk
						</app-button>
					</div>
				</div>
			</div>
		</app-panel>
	</div>

	<confirm-dialog
		:open="qtyDiscrepancyWarning"
		@confirmed="save"
		@canceled="qtyDiscrepancyWarning = false">
		Warning: Quantity discrepancies exist. Are you sure you want to Send to Hiredesk?
	</confirm-dialog>

	<slideover
		:title="editing?.damage_code ? 'Edit Damage Code(s)' : 'Add Damage Code(s)'"
		:open="editing !== null"
		:actions="[
			{
				text: 'Save',
				method: saveDamageCodeChanges,
				icon: 'save'
			}
		]"
		@close="editing = null">
		<div v-for="(code, index) in damageCodes" :key="index" class="mb-2 flex items-end">
			<app-input
				v-model="damageCodes[index]"
				:label="'Damage Code ' + [index + 1]"
				name="Damage Code"
				class="flex-grow" />
			<app-button icon="fa-trash" class="ml-2" @click="removeDamageCode(index)"
				>Remove</app-button
			>
		</div>

		<app-button class="mt-4" icon="fa-plus" @click="addDamageCode">Add Damage Code</app-button>
	</slideover>
	<add-edit-collection-note
		:collection="editingColNote"
		@updated="(notes) => (collection!.notes = notes)"
		@closed="editingColNote = null" />
</template>

<script lang="ts" setup>
	import { ref, computed, onMounted, watch } from 'vue'
	import CollectionDetailTable from '@/components/datatables/yard-app/collections/CollectionDetailTable.vue'
	import type { Collection } from '@/data/schema/collection'
	import type { CollectionItem } from '@/data/schema/collection-item'
	import { getAllCollectionItems } from '@/utils/contract-items'
	import { useRoute } from 'vue-router'
	import useToaster from '@/stores/toaster'
	import CollectionService from '@/services/collection'
	import useAuthStore from '@/stores/auth'
	import AddEditCollectionNote from '@/components/partials/cat2/AddEditCollectionNote.vue'

	// Resources
	const route = useRoute()
	const api = new CollectionService()
	const toaster = useToaster()
	const auth = useAuthStore()

	// Data
	const collectionNumber = ref()
	const loading = ref(true)
	const collection = ref<Collection>()
	const items = ref<Array<CollectionItem>>([])
	const editing = ref<CollectionItem | null>(null)
	const editingKit = ref<CollectionItem | null>(null)
	const damageCodes = ref<Array<string>>([])
	const editingColNote = ref<Collection | null>(null)
	const confirmed = ref(false)
	const qtyDiscrepancyWarning = ref(false)

	/**
	 * Get collection and collection item data
	 * @param init boolean - exclusive for onMounted
	 */
	async function getData() {
		loading.value = true
		confirmed.value = false

		api.get(collectionNumber.value, {
			withItems: true,
			operatorId: auth.user!.id
		})
			.then((res) => {
				collection.value = res.data.data

				if (collection.value && collection.value.items) {
					items.value = collection.value?.items.map((item) => ({
						...item,
						quantity_recorded: item.quantity_recorded ? item.quantity_recorded : 0,
						quantity_ok: item.quantity_ok ? item.quantity_ok : 0,
						quantity_damaged: item.quantity_damaged ? item.quantity_damaged : 0,
						changed: false
					}))

					items.value.forEach((item) => {
						if (item.kit_components) {
							item.kit_components = item.kit_components.map((kit) => ({
								...kit,
								quantity_recorded: kit.quantity_recorded
									? kit.quantity_recorded
									: 0,
								quantity_ok: kit.quantity_ok ? kit.quantity_ok : 0,
								quantity_damaged: kit.quantity_damaged ? kit.quantity_damaged : 0,
								changed: false
							}))
						}
					})
				}

				loading.value = false
			})
			.catch((error) => {
				console.error(error)
				loading.value = false
				toaster.error({
					text: 'An error occurred retrieving collection details'
				})
			})
	}

	watch(editing, (newVal) => {
		if (newVal) {
			if (editingKit.value) {
				damageCodes.value = editingKit.value.damage_code
					? editingKit.value.damage_code.split(',')
					: []
			} else {
				damageCodes.value = newVal.damage_code ? newVal.damage_code.split(',') : []
			}
		} else {
			damageCodes.value = []
		}
	})

	function addEditDamageCodes(item: CollectionItem, kit?: CollectionItem) {
		if (kit) {
			editing.value = item
			editingKit.value = kit
		} else {
			editing.value = item
		}
	}

	function saveDamageCodeChanges() {
		if (editing.value) {
			if (editingKit.value) {
				editingKit.value.damage_code = damageCodes.value.join(',')
				updated(editing.value, editingKit.value)
			} else {
				editing.value.damage_code = damageCodes.value.join(',')
				updated(editing.value)
			}
			editing.value = null
			editingKit.value = null
		}
	}

	function addDamageCode() {
		damageCodes.value.push('')
	}

	function removeDamageCode(index: number) {
		damageCodes.value.splice(index, 1)
	}

	/**
	 * Items have been updated
	 */
	function updated(item: CollectionItem, kit?: CollectionItem) {
		const itemIndex = items.value.findIndex((i) => i.id === item.id)

		if (itemIndex !== -1 && !kit) {
			items.value[itemIndex] = { ...items.value[itemIndex], ...item, changed: true }
		}

		if (itemIndex !== -1 && kit) {
			const kitIndex = items.value[itemIndex].kit_components!.findIndex(
				(i) => i.id === kit.id
			)
			items.value[itemIndex].kit_components![kitIndex] = {
				...items.value[itemIndex].kit_components![kitIndex],
				...kit,
				changed: true
			}
		}
	}

	function updatedNotes(item: CollectionItem, kitComponent?: CollectionItem) {
		let itemIndex = items.value.findIndex((i) => i.id === item.id)

		if (itemIndex !== -1 && !kitComponent) {
			items.value[itemIndex] = {
				...items.value[itemIndex],
				notes: item.notes,
				notes_status: 4
			}
		}

		if (itemIndex !== -1 && kitComponent) {
			const kitIndex = items.value[itemIndex].kit_components!.findIndex(
				(i) => i.id === kitComponent.id
			)
			items.value[itemIndex].kit_components![kitIndex] = {
				...items.value[itemIndex].kit_components![kitIndex],
				notes: kitComponent.notes,
				notes_status: 4
			}
		}
	}

	const changed = computed(() => {
		return items.value
			.flatMap((item) => [item, ...(item.kit_components || [])])
			.some((item) => item.changed)
	})

	function save(save = false) {
		const allItems = items.value.flatMap((item) => [item, ...(item.kit_components || [])])

		const sendItems = allItems
			.filter((item) => !save || item.changed === true)
			.map((item: CollectionItem) => {
				const quantity_ok = item.quantity_ok || 0
				const quantity_damaged = item.quantity_damaged || 0
				return {
					id: item.id,
					quantity_recorded: Number(quantity_ok) + Number(quantity_damaged),
					quantity_ok: quantity_ok,
					quantity_damaged: quantity_damaged,
					damage_code: item.damage_code
				}
			})

		const toasterMessage = save
			? { text: 'Saving collection item changes...', dismissable: false, timeout: 0 }
			: { text: 'Sending to hire desk...', dismissable: false, timeout: 0 }

		toaster.working(toasterMessage)

		api.updateItems(sendItems, { save: save })
			.then(() => {
				toaster.clear()
				toaster.success({ text: 'Changes saved successfully' })
				getData()
			})
			.catch((error) => {
				toaster.clear()
				toaster.error({ text: 'Error occurred updating collection' })
				console.error(error)
			})
	}

	function sendInterceptor() {
		let quantitiesMatch = true

		quantitiesMatch = getAllCollectionItems(items.value).every((item) => {
			if (item.type !== 'Kit' && item.quantity_on_hire) {
				return (
					Number(item.quantity_on_hire) ===
					Number(item.quantity_damaged) + Number(item.quantity_ok)
				)
			} else {
				return true
			}
		})

		quantitiesMatch ? save() : (qtyDiscrepancyWarning.value = true)
	}

	onMounted(() => {
		collectionNumber.value = route.params.id

		if (collectionNumber.value) {
			getData()
		}
	})
</script>
